import React, { useEffect } from "react";
import { ZonesHeader } from "./components/ZonesListHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "store";
import { deleteZoneItem, getZoneList } from "store/zones/slice";
import { Table } from "components/table/Table";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";

type Props = {};

const ZonesList: React.FC<Props> = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const zonesList = useAppSelector((state) => state.zones.list);
  const isZonesListFetched = useAppSelector(
    (state) => state.zones.isListFetched,
  );
  const processingItemId = useAppSelector(
    (state) => state.zones.processingItemId,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'zones',
    isListFetched: isZonesListFetched
  });

  const handleOpenEditModal = (zoneName: string) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.zone}&entityId=${zoneName}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.zone }));
  };

  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.zone}`);
    dispatch(openModal({ activeModal: ModalTypes.zone }));
  };

  const handleDeleteModal = async (zoneName: string) => {
    const result = await dispatch(deleteZoneItem(zoneName));

    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("zoneDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("zoneDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const permissions = usePermissionHelper();

  useEffect(() => {
    if (isZonesListFetched) return;

    dispatch(getZoneList());
  }, [isZonesListFetched]);

  return (
    <div className="page-list-wrapper">
      <ZonesHeader
        createModal={handleOpenCreateModal}
        refresh={refresh}
        isRefreshRequested={isRefreshRequested}
      />

      <div className="page-list-content">
        {!isZonesListFetched && (
          <DataTableSkeleton columnsCount={6} rowsCount={8} />
        )}

        {isZonesListFetched && zonesList.length === 0 && (
          <div className="game-table-wrap">
            <EmptyState message={t("noZonesFound")} />
          </div>
        )}

        {isZonesListFetched && zonesList.length > 0 && (
          <div className="game-table-wrap">
            <Table
              data={zonesList}
              idField="zoneName"
              hasCheckbox={false}
              onEdit={() => {}}
              onDelete={handleDeleteModal}
              columns={{
                sortingColumns: {
                  type: {
                    value: "type",
                    hasSorting: false,
                  },
                  status: {
                    value: "status",
                    hasSorting: false,
                  },
                },
              }}
              hasEdit={false}
              processingId={processingItemId}
              hasOptions={permissions?.delete}
              hasDelete={permissions?.delete}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ZonesList;
